/**
 * Query keys factory for TanStack Query
 * Centralized query key management for type-safe cache invalidation
 */

import type { ConversationStatus } from '@/types/conversations'
import type { PhoneCallsFilters } from '@/types/phone-calls'

/**
 * Conversation filters interface
 */
export interface ConversationFilters {
  status?: ConversationStatus
  source?: string
  category?: string
  tags?: string
  tag_ids?: string
  lead_source?: string
  phone_number_id?: string
  threads?: 'head' | 'all'
  pageSize?: number
}

/**
 * Query keys factory
 * Provides type-safe query keys for all queries
 */
export const queryKeys = {
  billing: {
    all: ['billing'] as const,
    accounts: () => [...queryKeys.billing.all, 'accounts'] as const,
    sales: (accountId: number) => [...queryKeys.billing.all, 'sales', accountId] as const,
    balances: (accountId: number) => [...queryKeys.billing.all, 'balances', accountId] as const,
    lapsed: (accountId: number) => [...queryKeys.billing.all, 'lapsed', accountId] as const,
    margin: (accountId: number, start: string, end: string, group: string) =>
      [...queryKeys.billing.all, 'margin', accountId, start, end, group] as const,
  },
  brain: {
    all: ['brain'] as const,
    conversations: () => [...queryKeys.brain.all, 'conversations'] as const,
    models: () => [...queryKeys.brain.all, 'models'] as const,
  },
  meta: {
    all: ['meta'] as const,
    facebookStatus: () => [...queryKeys.meta.all, 'facebook-status'] as const,
    whatsappStatus: () => [...queryKeys.meta.all, 'whatsapp-status'] as const,
    instagramStatus: () => [...queryKeys.meta.all, 'instagram-status'] as const,
    messengerStatus: () => [...queryKeys.meta.all, 'messenger-status'] as const,
    commentMedia: (channel: string) => [...queryKeys.meta.all, 'comment-media', channel] as const,
    comments: (channel: string, mediaId: string) => [...queryKeys.meta.all, 'comments', channel, mediaId] as const,
    commentAutomation: (channel: string) => [...queryKeys.meta.all, 'comment-automation', channel] as const,
    utilityTemplates: () => [...queryKeys.meta.all, 'messenger-utility-templates'] as const,
    marketingSubscribers: () => [...queryKeys.meta.all, 'messenger-marketing-subscribers'] as const,
    adAccounts: () => [...queryKeys.meta.all, 'ad-accounts'] as const,
    businesses: () => [...queryKeys.meta.all, 'businesses'] as const,
    managedApps: () => [...queryKeys.meta.all, 'managed-apps'] as const,
    catalogs: (businessId: string) => [...queryKeys.meta.all, 'catalogs', businessId] as const,
    catalogProducts: (catalogId: string) => [...queryKeys.meta.all, 'catalog-products', catalogId] as const,
    instagramInsights: () => [...queryKeys.meta.all, 'instagram-insights'] as const,
  },
  conversations: {
    all: ['conversations'] as const,
    lists: () => [...queryKeys.conversations.all, 'list'] as const,
    list: (filters: ConversationFilters = {}) => 
      [...queryKeys.conversations.lists(), filters] as const,
    details: () => [...queryKeys.conversations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.conversations.details(), id] as const,
    messages: (id: string, page?: number, pageSize?: number) => 
      [...queryKeys.conversations.detail(id), 'messages', { page, pageSize }] as const,
    conversionEvents: (id: string) => [...queryKeys.conversations.detail(id), 'conversion-events'] as const,
    contactProfile: (id: string) => [...queryKeys.conversations.detail(id), 'contact-profile'] as const,
  },
  phoneCalls: {
    all: ['phone-calls'] as const,
    lists: () => [...queryKeys.phoneCalls.all, 'list'] as const,
    list: (filters: PhoneCallsFilters = {}) => 
      [...queryKeys.phoneCalls.lists(), filters] as const,
    details: () => [...queryKeys.phoneCalls.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.phoneCalls.details(), id] as const,
  },
  documents: {
    all: ['documents'] as const,
    raw: (filters?: { category?: string; subcategory?: string }) =>
      [...queryKeys.documents.all, 'raw', filters || {}] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    list: (filters?: { category?: string; subcategory?: string }) => 
      [...queryKeys.documents.lists(), filters || {}] as const,
    details: () => [...queryKeys.documents.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.documents.details(), id] as const,
    agents: (id: number) => [...queryKeys.documents.all, 'agents', id] as const,
  },
  // pantallas-abilities-y-skills
  abilities: {
    all: ['abilities'] as const,
    ofAgent: (agentId: string) => ['abilities', 'agent', agentId] as const,
  },
  skills: {
    all: ['skills'] as const,
  },
  urls: {
    all: ['urls'] as const,
    lists: () => [...queryKeys.urls.all, 'list'] as const,
    list: (filters?: { category?: string; subcategory?: string }) => 
      [...queryKeys.urls.lists(), filters || {}] as const,
    details: () => [...queryKeys.urls.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.urls.details(), id] as const,
  },
  knowledge: {
    all: ['knowledge'] as const,
    categoriesBundle: () => [...queryKeys.knowledge.all, 'categories-bundle'] as const,
    directoriesTree: () => [...queryKeys.knowledge.all, 'directories-tree'] as const,
    settings: () => [...queryKeys.knowledge.all, 'settings'] as const,
  },
  documentSearch: {
    all: ['document-search'] as const,
    search: (query: string, limit?: number, filters?: Record<string, unknown>) => 
      [...queryKeys.documentSearch.all, 'search', { query, limit, filters }] as const,
  },
} as const
